import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";

import { GoogleSocialButton, FacebookSocialButton, AppleSocialButton } from "@components/business/social";

import { useTranslation } from "react-i18next";
import { OpenAPI } from "@shared/services";
import { setState } from "@shared/utils";
import { useOAuth2 } from "@shared/contexts";
import { checkEndpointHealth } from "@shared/utils/endpoint/endpointHealthCheck";
import { getSocialLoginOrder, SocialProvider } from "@shared/utils/device/getPlatform";
import { isAppleSignInEnabled } from "@shared/utils/environment";

/** Renders a dismissible inline warning shown when a provider is unavailable. */
const ProviderError: React.FC<{ message: string; onDismiss: () => void }> = ({ message, onDismiss }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between border border-yellow-300 bg-yellow-50 px-3 py-2 mb-1.5 text-[13px] text-yellow-800 dark:border-yellow-600 dark:bg-yellow-900/20 dark:text-yellow-200">
      <span>{message}</span>
      <button
        type="button"
        onClick={onDismiss}
        className="ml-2 text-yellow-600 hover:text-yellow-800 dark:text-yellow-400"
        aria-label={t('help.closeAriaLabel')}
      >
        &times;
      </button>
    </div>
  );
};

const ExternalAuthentication: React.FC = React.memo(() => {
  const { t } = useTranslation();
  const { state } = useOAuth2();

  const [loadingProvider, setLoadingProvider] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const originalLocationRef = useRef<string>(window.location.href);

  // Sign in with Apple is gated behind the APPLE_SIGNIN_ENABLED runtime flag
  // and is DISABLED BY DEFAULT. When off, the Apple button is not rendered and
  // the platform-aware ordering falls back to Google-only — the login screen
  // looks exactly as it did before Apple was introduced.
  const appleEnabled = useMemo(() => isAppleSignInEnabled(), []);

  // Platform-aware ordering of the primary social providers:
  //   iOS → Apple first, Android/desktop → Google first.
  // With Apple disabled, Apple is filtered out so only Google remains.
  const socialOrder: SocialProvider[] = useMemo(() => {
    const order = getSocialLoginOrder();
    return appleEnabled ? order : order.filter((provider) => provider !== "apple");
  }, [appleEnabled]);

  const startProviderLogin = useCallback(
    async (provider: string, unavailableKey: string) => {
      setLoadingProvider(provider);
      setErrors((prev) => ({ ...prev, [provider]: null }));

      try {
        const endpointUrl = `${OpenAPI.BASE}/oauth2/oidc/${provider}/login${window.location.search}`;
        const isHealthy = await checkEndpointHealth(endpointUrl);

        if (isHealthy) {
          setState(state);
          window.location.href = endpointUrl;
          return;
        }
        setErrors((prev) => ({ ...prev, [provider]: t(unavailableKey) }));
        setLoadingProvider(null);
      } catch (error) {
        console.error(`${provider} authentication failed:`, error);
        setErrors((prev) => ({ ...prev, [provider]: t(unavailableKey) }));
        setLoadingProvider(null);
      }
    },
    [state, t],
  );

  const dismissError = useCallback((provider: string) => {
    setErrors((prev) => ({ ...prev, [provider]: null }));
  }, []);

  const isOnOriginalPage = useCallback((currentLocation: string) => {
    return currentLocation === originalLocationRef.current ||
           currentLocation.includes(window.location.origin);
  }, []);

  const shouldResetLoadingState = useCallback((currentLocation: string) => {
    const onOriginal = isOnOriginalPage(currentLocation);
    const notOAuthRedirect = !currentLocation.includes('/oauth2/oidc/');
    return onOriginal && notOAuthRedirect;
  }, [isOnOriginalPage]);

  useEffect(() => {
    const handlePageShow = () => {
      if (!shouldResetLoadingState(window.location.href)) return;
      if (loadingProvider) setLoadingProvider(null);
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [loadingProvider, shouldResetLoadingState]);

  const renderGoogle = () => (
    <div key="google">
      {errors.google && <ProviderError message={errors.google} onDismiss={() => dismissError('google')} />}
      <GoogleSocialButton
        onClick={() => startProviderLogin('google', 'login.identity.googleUnavailable')}
        disabled={loadingProvider !== null && loadingProvider !== 'google'}
        loading={loadingProvider === 'google'}
        text={t('login.identity.continueWithGoogle')}
      />
    </div>
  );

  const renderApple = () => (
    <div key="apple">
      {errors.apple && <ProviderError message={errors.apple} onDismiss={() => dismissError('apple')} />}
      <AppleSocialButton
        onClick={() => startProviderLogin('apple', 'login.identity.appleUnavailable')}
        disabled={loadingProvider !== null && loadingProvider !== 'apple'}
        loading={loadingProvider === 'apple'}
        text={t('login.identity.continueWithApple')}
      />
    </div>
  );

  const providerRenderers: Record<SocialProvider, () => React.ReactNode> = {
    google: renderGoogle,
    apple: renderApple,
  };

  return (
    <div className="flex flex-col gap-2">
      {socialOrder.map((provider) => providerRenderers[provider]())}
      <div key="facebook">
        {errors.facebook && <ProviderError message={errors.facebook} onDismiss={() => dismissError('facebook')} />}
        <FacebookSocialButton
          onClick={() => startProviderLogin('facebook', 'login.identity.facebookUnavailable')}
          disabled={loadingProvider !== null && loadingProvider !== 'facebook'}
          loading={loadingProvider === 'facebook'}
          text={t('login.identity.continueWithFacebook')}
        />
      </div>
    </div>
  );
});

ExternalAuthentication.displayName = 'ExternalAuthentication';

export default ExternalAuthentication;
